import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonContent, IonModal } from "@ionic/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

const CONTENT_PX = 460;
const KEYBOARD_PX = 311;

interface AmendMacrosModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (values: NutritionValues) => Promise<void>;
  isSaving: boolean;
  mealName: string;
  calories: number;
  proteinG: number;
  carbsG: number;
  fatsG: number;
}

export interface NutritionValues {
  calories: number;
  proteinG: number;
  carbsG: number;
  fatsG: number;
}

export const AmendMacrosModal = memo(function AmendMacrosModal({
  isOpen,
  onDismiss,
  onSave,
  isSaving,
  mealName,
  calories,
  proteinG,
  carbsG,
  fatsG,
}: AmendMacrosModalProps) {
  const caloriesInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const breakpoint = useMemo(() => {
    const screenHeight = typeof window !== "undefined" ? window.screen.height : 844;
    return parseFloat(Math.min((CONTENT_PX + KEYBOARD_PX) / screenHeight, 0.95).toFixed(3));
  }, []);

  const [caloriesValue, setCaloriesValue] = useState(String(calories));
  const [proteinValue, setProteinValue] = useState(String(proteinG));
  const [carbsValue, setCarbsValue] = useState(String(carbsG));
  const [fatsValue, setFatsValue] = useState(String(fatsG));

  useEffect(() => {
    if (!isOpen) return;

    setCaloriesValue(String(calories));
    setProteinValue(String(proteinG));
    setCarbsValue(String(carbsG));
    setFatsValue(String(fatsG));
  }, [isOpen, calories, proteinG, carbsG, fatsG]);

  const parsedValues = [caloriesValue, proteinValue, carbsValue, fatsValue].map((value) =>
    value.trim() === "" ? Number.NaN : Number(value),
  );
  const canSave = parsedValues.every((value) => Number.isFinite(value) && value >= 0);

  const handleSave = async () => {
    if (!canSave || isSaving || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    try {
      await onSave({
        calories: parsedValues[0],
        proteinG: parsedValues[1],
        carbsG: parsedValues[2],
        fatsG: parsedValues[3],
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      onDidPresent={() => caloriesInputRef.current?.focus()}
      initialBreakpoint={breakpoint}
      breakpoints={[0, breakpoint]}
      handle={true}
      style={{ "--border-radius": "56px" } as React.CSSProperties}
    >
      <IonContent scrollY={false}>
        <div className="flex flex-col px-[35px] pt-6 pb-8">
          <div className="flex flex-col items-center gap-1">
            <h2 className={cn(textVariants.title3, "text-center font-bold")}>
              Edit Calories and macros
            </h2>
            <p className={cn(textVariants.footnote, "text-center opacity-60")}>{mealName}</p>
          </div>

          <div className="mt-9 flex flex-col gap-11">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold text-black">Kcals</label>
              <input
                ref={caloriesInputRef}
                type="number"
                inputMode="numeric"
                min="0"
                step="any"
                value={caloriesValue}
                onChange={(event) => setCaloriesValue(event.target.value)}
                className="h-[46px] w-full rounded-[14px] border border-[#620ad5] px-3 text-sm font-medium shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] outline-none"
              />
            </div>

            <div className="flex flex-col gap-11">
              <div className="flex gap-2">
                <MacroInput label="Protein" value={proteinValue} onChange={setProteinValue} />
                <MacroInput label="Carbs" value={carbsValue} onChange={setCarbsValue} />
                <MacroInput label="Fats" value={fatsValue} onChange={setFatsValue} />
              </div>

              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!canSave || isSaving}
                className="h-[46px] w-full rounded-[14px] bg-[#60418a] text-base font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
});

interface MacroInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const MacroInput = ({ label, value, onChange }: MacroInputProps) => (
  <div className="flex flex-1 flex-col gap-2">
    <label className="text-sm font-bold text-black">{label}</label>
    <input
      type="number"
      inputMode="numeric"
      min="0"
      step="any"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-[46px] w-full rounded-[14px] border border-[#d6dbe7] px-3 text-sm font-medium shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] outline-none focus:border-[#620ad5]"
    />
  </div>
);
